"use client";

import { useEffect, useRef } from "react";

interface Particle {
    x: number;
    y: number;
    baseX: number;
    baseY: number;
    vx: number;
    vy: number;
    size: number;
    color: string;
    alpha: number;
    angle: number;
    speed: number;
}

interface ClickBurst {
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    alpha: number;
}

export default function InteractiveServiceBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouseRef = useRef({ x: -1000, y: -1000, active: false });
    const particlesRef = useRef<Particle[]>([]);
    const burstsRef = useRef<ClickBurst[]>([]);
    const animationFrameRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Set high DPI support
        const resize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const dpr = window.devicePixelRatio || 1;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.scale(dpr, dpr);

            // Re-initialize particles to match screen size
            initParticles(width, height);
        };

        const initParticles = (width: number, height: number) => {
            const particles: Particle[] = [];
            // Determine count based on screen size (approx. 1 particle per 15000 pixels)
            const count = Math.min(100, Math.floor((width * height) / 15000));

            for (let i = 0; i < count; i++) {
                const x = Math.random() * width;
                const y = Math.random() * height;
                particles.push({
                    x,
                    y,
                    baseX: x,
                    baseY: y,
                    vx: (Math.random() - 0.5) * 0.4,
                    vy: (Math.random() - 0.5) * 0.4,
                    size: Math.random() * 2 + 1,
                    color: "rgba(147, 51, 234, ", // Purple base, will attach alpha
                    alpha: Math.random() * 0.4 + 0.1,
                    angle: Math.random() * Math.PI * 2,
                    speed: Math.random() * 0.02 + 0.005,
                });
            }
            particlesRef.current = particles;
        };

        resize();
        window.addEventListener("resize", resize);

        // Mouse Move handler
        const handleMouseMove = (e: MouseEvent) => {
            mouseRef.current.x = e.clientX;
            mouseRef.current.y = e.clientY;
            mouseRef.current.active = true;
        };

        const handleMouseLeave = () => {
            mouseRef.current.active = false;
            mouseRef.current.x = -1000;
            mouseRef.current.y = -1000;
        };

        // Click Shockwave Burst handler
        const handleMouseClick = (e: MouseEvent) => {
            burstsRef.current.push({
                x: e.clientX,
                y: e.clientY,
                radius: 0,
                maxRadius: 180,
                alpha: 0.6,
            });

            // Apply physical push away to particles
            const clickX = e.clientX;
            const clickY = e.clientY;

            particlesRef.current.forEach((p) => {
                const dx = p.x - clickX;
                const dy = p.y - clickY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 200) {
                    const force = (200 - dist) / 15;
                    const angle = Math.atan2(dy, dx);
                    p.vx += Math.cos(angle) * force;
                    p.vy += Math.sin(angle) * force;
                }
            });
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseleave", handleMouseLeave);
        window.addEventListener("click", handleMouseClick);

        // Animation Loop
        const animate = () => {
            const width = canvas.width / (window.devicePixelRatio || 1);
            const height = canvas.height / (window.devicePixelRatio || 1);

            ctx.clearRect(0, 0, width, height);

            // Draw Background Gradients / Ambience
            const mouseX = mouseRef.current.x;
            const mouseY = mouseRef.current.y;

            if (mouseRef.current.active) {
                // Glow at mouse position
                const radialGlow = ctx.createRadialGradient(
                    mouseX,
                    mouseY,
                    10,
                    mouseX,
                    mouseY,
                    250
                );
                radialGlow.addColorStop(0, "rgba(243, 244, 246, 0.45)");
                radialGlow.addColorStop(0.5, "rgba(147, 51, 234, 0.03)");
                radialGlow.addColorStop(1, "transparent");
                ctx.fillStyle = radialGlow;
                ctx.fillRect(0, 0, width, height);
            }

            // Update & Draw Click Bursts
            burstsRef.current.forEach((burst, index) => {
                burst.radius += (burst.maxRadius - burst.radius) * 0.1;
                burst.alpha -= 0.015;

                if (burst.alpha <= 0) {
                    burstsRef.current.splice(index, 1);
                    return;
                }

                ctx.strokeStyle = `rgba(147, 51, 234, ${burst.alpha})`;
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(burst.x, burst.y, burst.radius, 0, Math.PI * 2);
                ctx.stroke();

                // Outer faint ring
                ctx.strokeStyle = `rgba(59, 130, 246, ${burst.alpha * 0.4})`;
                ctx.beginPath();
                ctx.arc(burst.x, burst.y, burst.radius * 1.2, 0, Math.PI * 2);
                ctx.stroke();
            });

            // Update & Draw Particles
            const particles = particlesRef.current;
            particles.forEach((p, idx) => {
                // Wave flow movement
                p.angle += p.speed;
                p.x += p.vx + Math.cos(p.angle) * 0.1;
                p.y += p.vy + Math.sin(p.angle) * 0.1;

                // Friction decay for impulses
                p.vx *= 0.96;
                p.vy *= 0.96;

                // Mouse interaction (repel & draw connection)
                if (mouseRef.current.active) {
                    const dx = p.x - mouseX;
                    const dy = p.y - mouseY;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < 180) {
                        // Repel force
                        const force = (180 - dist) / 180;
                        const angle = Math.atan2(dy, dx);
                        p.x += Math.cos(angle) * force * 1.5;
                        p.y += Math.sin(angle) * force * 1.5;

                        // Draw connector line to mouse
                        const alpha = (1 - dist / 180) * 0.15;
                        ctx.strokeStyle = `rgba(147, 51, 234, ${alpha})`;
                        ctx.lineWidth = 0.8;
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(mouseX, mouseY);
                        ctx.stroke();
                    }
                }

                // Bounds check with soft bounce
                if (p.x < 0) {
                    p.x = 0;
                    p.vx *= -1;
                } else if (p.x > width) {
                    p.x = width;
                    p.vx *= -1;
                }
                if (p.y < 0) {
                    p.y = 0;
                    p.vy *= -1;
                } else if (p.y > height) {
                    p.y = height;
                    p.vy *= -1;
                }

                // Draw Particle
                ctx.fillStyle = `${p.color}${p.alpha})`;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();

                // Draw connections between neighboring particles
                for (let j = idx + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dx = p.x - p2.x;
                    const dy = p.y - p2.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < 110) {
                        const alpha = (1 - dist / 110) * 0.08;
                        ctx.strokeStyle = `rgba(163, 113, 247, ${alpha})`;
                        ctx.lineWidth = 0.6;
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.stroke();
                    }
                }
            });

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseleave", handleMouseLeave);
            window.removeEventListener("click", handleMouseClick);
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0 bg-transparent transition-opacity duration-500"
            style={{ mixBlendMode: "multiply" }}
        />
    );
}
